import AppTypes from './app.types';
import { getParameterByName, getSubdomain } from '../../utils';
import * as CompanyAPI from 'services/api/api.company';
import { getContactLog, getDefaultContactLog } from 'services/api/api.contactLog';
import { Instance, InstanceSettings, AppConfig } from './app.state';
import RootState from '../state';
import { ThunkDispatch } from 'redux-thunk';
import { AxiosError } from 'axios';
import { logout } from 'store/user/user.actions';
import { CONTACT_LOG } from 'constants/index';
import { IContactLog } from 'views/AdminSettings/ContactLogs/types';
import { IAppFeature } from 'store/settings/settings.state';
import { getWebsocketUrl } from 'services/api/api.chat';
import bchat from '@missionlabs/smartagent-bchat'
import { AppFeatures } from 'hooks/useHasFeature';
import { init as initProviders, gtag } from '@missionlabs/analytics-core';

interface AppError {
    message: string;
}

interface PlutingInstalledAction {
    name: string;
    iframe: HTMLIFrameElement;
}

interface UpdatePluginTitleTagAction {
    name: string;
    tag: string;
}

export type AppAction =
    | { type: typeof AppTypes.INIT_APP_FAIL; payload: AppError }
    | { type: typeof AppTypes.INIT_APP_LOADING }
    | { type: typeof AppTypes.INIT_APP_SUCCESS; payload: any }
    | { type: typeof AppTypes.SELECT_INSTANCE; payload: { instance: Instance, appConfig: AppConfig} }
    | { type: typeof AppTypes.SET_COMPANY_GROUPS; payload: any[] }
    | { type: typeof AppTypes.CLEAR_INSTANCE }
    | { type: typeof AppTypes.TOGGLE_IFRAME }
    | { type: typeof AppTypes.SHOW_PLUGIN; payload: string }
    | { type: typeof AppTypes.HIDE_PLUGIN; payload: string }
    | { type: typeof AppTypes.RESET_PLUGIN; payload: string }
    | { type: typeof AppTypes.UPDATE_PLUGIN_TITLE_TAG; payload: UpdatePluginTitleTagAction }
    | { type: typeof AppTypes.PLUGIN_INSTALLED; payload: PlutingInstalledAction }
    | { type: typeof AppTypes.REPORT_ISSUE; payload: string }
    | { type: typeof AppTypes.LOADING_FORM; payload: string }
    | { type: typeof AppTypes.FORM_LOADED; payload: { name: string; form: any } }
    | { type: typeof AppTypes.FORM_NOT_FOUND; payload: string }
    | { type: typeof AppTypes.UPDATE_INSTANCE_SETTINGS; payload: InstanceSettings }
    | { type: typeof AppTypes.UPDATE_KEYWORDS_ERROR; payload: boolean}
    | { type: typeof AppTypes.UPDATE_KEYWORDS_LOADING; payload: boolean}
    | { type: typeof AppTypes.UPDATE_KEYWORDS_SUCCESS; payload: boolean}
    | { type: typeof AppTypes.FORM_ERROR; payload: string }
    | { type: typeof AppTypes.CCP_LOGOUT};

export function appError(message?: string): AppAction {
    return {
        type: AppTypes.INIT_APP_FAIL,
        payload: {
            message: message || '',
        },
    };
}

export function setCompanyGroups(groups: any[]) : AppAction {
    return {
        type: AppTypes.SET_COMPANY_GROUPS,
        payload: groups
    }
}

export function initApp() {
    let companyID = getParameterByName('companyID') || getSubdomain();
    if (!companyID) {
        return appError();
    }
    return async (
        dispatch: ThunkDispatch<RootState, undefined, AppAction>,
        getState: () => RootState
    ) => {

        dispatch<AppAction>({
            type: AppTypes.INIT_APP_LOADING,
        });

        try {
            const groupCompany = getParameterByName('groupCompany');

            if (groupCompany) {
                companyID = groupCompany;
            } else {
                // check if we're in a group
                const groupSplit = companyID!.split('-');

                if (groupSplit[1] === 'group') {
                    const groups = await CompanyAPI.getCompanyGroup(groupSplit[0]);
                    return dispatch(setCompanyGroups(groups.data))
                }

            }

            const company = await CompanyAPI.getCompany(companyID!);

            const chatFeature = company.features.find((feature: IAppFeature) => feature.ID === AppFeatures.CHAT);

            if (chatFeature) {

                try {
                    const { websocketUrl, apiKey } = await getWebsocketUrl(company.ID);

                    if (websocketUrl) {
                        window.bchat = await bchat({
                            connectionTimeoutMs: 7000,
                            wsUrl: websocketUrl + '?Authorization=' + apiKey,
                            onReconnect: () => {
                                // refresh ws connectionId for each chat
                                getState().chat.connections.forEach((chat) => {
                                    window.bchat.getChatSession(chat.initialContactId ?? chat.id, "AGENT")
                                })
                            }
                        });
                    }
                } catch (err) {
                    console.log('error getting bchat websocket url, cannot initialise bchat', err);
                }
            }

            const analyticsFeature = company.features.find((feature: IAppFeature) => feature.ID === AppFeatures.COLLECT_ANALYTICS);

            if (analyticsFeature) {
                initProviders([
                    {
                        provider: gtag,
                        options: {
                            id: 'G-2SMJ7QV0BK',
                            debug: process.env.REACT_APP_ENV === 'dev',
                            configSettings: {
                                send_page_view: false,
                                app_name: 'smartagent-app',
                            },
                        },
                    },
                ]);
            }

            const identityManagement = getParameterByName('identityManagement');

            if(identityManagement) company.identityManagement = identityManagement;
            // here the data about the company gets into redux
            dispatch({
                type: AppTypes.INIT_APP_SUCCESS,
                payload: company,
            });

            const instanceKey = getParameterByName('instanceKey');

            const instances = company.instances;

            //If we have a refreshToken then use the instance in store
            const isAuthed = !!getState().auth.refreshToken;

            const instance =
                instances.length === 1
                    ? instances[0]
                    : instanceKey
                    ? instances.find((inst: any) => inst.key === instanceKey)
                    : isAuthed
                    ? getState().app.instance
                    : null;

            //No instance selected to let SelectInstance handle this
            if (!instance) {
                return dispatch(clearInstance());
            }
            return dispatch(selectInstance(instance, company.appConfig));
        } catch (err) {
            console.warn(err);
            dispatch(appError());
        }
    };
}

export function selectInstance(instance: Instance, appConfig: AppConfig): AppAction {
    return {
        type: AppTypes.SELECT_INSTANCE,
        payload: {
            instance,
            appConfig
        },
    };
}

export function clearInstance(): AppAction {
    return {
        type: AppTypes.CLEAR_INSTANCE,
    };
}

export function toggleIframe(): AppAction {
    return {
        type: AppTypes.TOGGLE_IFRAME,
    };
}

export function showPlugin(name: string): AppAction {
    return {
        type: AppTypes.SHOW_PLUGIN,
        payload: name,
    };
}

export function hidePlugin(name: string): AppAction {
    return {
        type: AppTypes.HIDE_PLUGIN,
        payload: name,
    };
}

export function resetPlugin(name: string): AppAction {
    return {
        type: AppTypes.RESET_PLUGIN,
        payload: name,
    };
}

export function pluginInstalled(name: string, iframe: HTMLIFrameElement): AppAction {
    return {
        type: AppTypes.PLUGIN_INSTALLED,
        payload: {
            name,
            iframe,
        },
    };
}

export function updateTitleTag(name: string, tag: string): AppAction {
    return {
        type: AppTypes.UPDATE_PLUGIN_TITLE_TAG,
        payload: {
            name,
            tag,
        },
    };
}

export function reportIssue(description: string): AppAction {
    return {
        type: AppTypes.REPORT_ISSUE,
        payload: description,
    };
}

export function loadForm(name: string, formGroup?: string) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, AppAction>,
        getState: () => RootState
    ) => {
        const { app: { instance, ID, features }, auth: { token } } = getState();

        let key = formGroup ? `${name}-${formGroup}` : name;
        const isContactLogsFeatureEnabled = !!features.find(feature => feature.ID === 'contact-logs');
        if (key === CONTACT_LOG && isContactLogsFeatureEnabled && !formGroup) key = `${name}-default`

        try {
            dispatch({
                type: AppTypes.LOADING_FORM,
                payload: key,
            });

            let form;
            if (name === CONTACT_LOG) form = await handleContactLogForm(instance!, ID, token!, key, isContactLogsFeatureEnabled, formGroup);
            else form = await CompanyAPI.getFormTemplate(ID, name, token!, formGroup);

            if(!form){
                return dispatch({
                    type: AppTypes.FORM_NOT_FOUND,
                    payload: key,
                });
            }
            else{
                dispatch({
                    type: AppTypes.FORM_LOADED,
                    payload: {
                        name: key,
                        form,
                    },
                });
            }

        } catch (err) {
            const error = err as AxiosError;

            if (error.response?.status === 403) return dispatch(logout())

            if (error.response?.status === 404) {
                return dispatch({
                    type: AppTypes.FORM_NOT_FOUND,
                    payload: key,
                });
            }

            dispatch({
                type: AppTypes.FORM_ERROR,
                payload: key,
            });
        }
    };
}

const handleContactLogForm = async (instance: Instance, ID: string, token: string, key: string, isContactLogsFeatureEnabled: boolean, formGroup?: string): Promise<IContactLog> => {
    let contactLog: IContactLog;
    // Feature flag is on.
    if (isContactLogsFeatureEnabled) {
        // Either get default or specified log.
        if (key === `${CONTACT_LOG}-default`) {
            contactLog = await getDefaultContactLog(ID, instance?.ID!, token!);
        } else {
            contactLog = await getContactLog(ID, instance?.ID!, formGroup!, token!);
        }
    // Feature flag is off.
    } else {
        // Either get from S3 with added path specified as the formGroup, or just contact-log.json.
        if (formGroup) {
            contactLog = await getContactLog(ID, instance?.ID!, CONTACT_LOG, token!, formGroup);
        } else {
            contactLog = await getContactLog(ID, instance?.ID!, CONTACT_LOG, token!);
        }
    }
    return contactLog;
}


export function updateKeywordsError(status: boolean): AppAction {
    return {
        type: AppTypes.UPDATE_KEYWORDS_ERROR,
        payload: status,
    };
}

export function updateInstanceSettings(settings: InstanceSettings) {
    return async (
        dispatch: ThunkDispatch<RootState, undefined, AppAction>,
        getState: () => RootState
    ) => {
        try {
            dispatch({
                type: AppTypes.UPDATE_KEYWORDS_LOADING,
                payload: true
            })

            const companyID = getState().app.ID;
            const token = getState().auth.token!;
            const instanceID = getState().app.instance?.ID!

            dispatch({
                type: AppTypes.UPDATE_INSTANCE_SETTINGS,
                payload: settings
            })

            const response = await CompanyAPI.putInstanceSettings(companyID, token, instanceID, settings);

            dispatch({
                type: AppTypes.UPDATE_KEYWORDS_SUCCESS,
                payload: response.status === 200
            })

        } catch (err) {
            console.log("error updating settings", err);
            const e = err as AxiosError;

            if (e.response?.status === 403) return dispatch(logout());
            if (e) return dispatch(updateKeywordsError(true));
        }

        finally{
            dispatch({
                type: AppTypes.UPDATE_KEYWORDS_LOADING,
                payload: false
            })
        }
    }
}